import React, { useState, useMemo, useEffect } from 'react';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, RefreshCw, Search, Filter, Globe, Calendar, Key, Server, Eye, EyeOff, Settings, Columns, FileCode2, PlugZap, Loader2, CheckCircle2, XCircle, Briefcase, Palette, AlertTriangle, Ban } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useWebsites, Website, NewWebsite } from '@/hooks/useWebsites';
import { useHostings } from '@/hooks/useHostings';
import { WebsiteSchemaDialog } from '@/components/WebsiteSchemaDialog';
import { testWebsiteConnection, testWebsitesBulk } from '@/services/wpConnectionTest';
import DomainExpirySync from '@/components/domains/DomainExpirySync';
import { RejectedDomainsDialog, RejectedDomainsBanner } from '@/components/domains/RejectedDomains';
import { useDomainRejections } from '@/hooks/useDomainRejections';
import { websiteDomainKey } from '@/services/aftermarketService';
import { useCanAccessSideprojects } from '@/hooks/useCanAccessSideprojects';
import { useWorkspace } from '@/hooks/useWorkspace';

interface WebsitesTableProps {
  className?: string;
}

const WebsitesTable: React.FC<WebsitesTableProps> = ({ className }) => {
  const { websites, loading, createWebsite, createWebsitesBulk, updateWebsite, deleteWebsite, renewWebsiteExpiry, patchWebsiteLocal, refreshWebsites } = useWebsites();
  const { hostings } = useHostings();
  const { rejections, replaceList, removeRejection } = useDomainRejections();
  const { canAccess: canSideprojects } = useCanAccessSideprojects();
  const { workspace } = useWorkspace();

  // State for filters
  const [searchTerm, setSearchTerm] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Filtr workspace startuje z GLOBALNEGO przełącznika (sidebar footer) i podąża
  // za nim — tryb Sponsoraki pokazuje tylko sideprojects, Firma tylko firmowe.
  // Dropdown w tabeli zostaje jako ręczny override (do następnej zmiany przełącznika).
  const [workspaceFilter, setWorkspaceFilter] = useState<'all' | 'firma' | 'sideprojects'>(workspace);
  useEffect(() => {
    setWorkspaceFilter(workspace);
  }, [workspace]);

  // Test połączenia WP — per-domena spinner + bulk progress
  const [testingIds, setTestingIds] = useState<Set<string>>(new Set());
  const [bulkTesting, setBulkTesting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  // Bulk pomija domeny z connection_status='ok' — żeby nie przepalać testów na
  // już potwierdzonych. Odznacz żeby wymusić re-test wszystkich przefiltrowanych.
  const [skipConfirmed, setSkipConfirmed] = useState(true);

  // State for column visibility
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    url: true,
    login: true,
    password: true,
    api_key: true,
    provider: true,
    category: true,
    workspace: true,
    connection: true,
    expiry_date: true,
  });

  // State for forms
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isBulkAddDialogOpen, setIsBulkAddDialogOpen] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState<Website | null>(null);
  const [schemaDialogWebsite, setSchemaDialogWebsite] = useState<Website | null>(null);
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());
  const [showPasswordInForm, setShowPasswordInForm] = useState(false);
  const [formData, setFormData] = useState<NewWebsite>({
    name: '',
    url: '',
    login: '',
    password: '',
    api_key: '',
    expiry_date: '',
    provider: '',
    category: '',
    workspace: 'firma'
  });
  const [bulkText, setBulkText] = useState('');

  const getSafeUrl = (rawUrl: string) => {
    const sanitized = sanitizeUrl(rawUrl);
    return sanitized === 'about:blank' ? null : sanitized;
  };

  // Get unique categories for filter
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(websites.map(w => w.category).filter(Boolean)));
    return uniqueCategories;
  }, [websites]);

  // Helper functions
  const getDaysUntilExpiry = (expiryDate: string) => {
    if (!expiryDate) return Infinity;

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for consistent comparison

    const expiry = new Date(expiryDate);
    if (isNaN(expiry.getTime())) return Infinity; // Invalid date

    expiry.setHours(0, 0, 0, 0); // Reset time to start of day

    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // ===== Domeny odrzucone =====
  // Dopasowanie po tym samym kluczu co sync AfterMarket (bez protokołu/www),
  // żeby "przyklad.pl" z listy trafiało w "https://www.przyklad.pl/" w bazie.
  const rejectedSet = useMemo(() => new Set(rejections.map(r => r.domain)), [rejections]);
  const isRejected = (w: Website) => rejectedSet.has(websiteDomainKey(w));
  // Domeny odrzucone, które WCIĄŻ są w panelu — to one zapalają baner.
  const rejectedInPanel = useMemo(() => websites.filter(isRejected), [websites, rejectedSet]);

  // Filtered websites
  const filteredWebsites = useMemo(() => {
    return websites.filter(website => {
      const matchesSearch = website.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          website.url.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (website.category?.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesExpiry = expiryFilter === 'all' ||
                          (expiryFilter === 'expiring-soon' && website.expiry_date && getDaysUntilExpiry(website.expiry_date) > 0 && getDaysUntilExpiry(website.expiry_date) <= 30) ||
                          (expiryFilter === 'expired' && website.expiry_date && getDaysUntilExpiry(website.expiry_date) < 0) ||
                          (expiryFilter === 'rejected' && isRejected(website)) ||
                          (expiryFilter === 'not-on-account' && website.expiry_source === 'not_on_account');

      const matchesCategory = categoryFilter === 'all' || website.category === categoryFilter;

      const matchesWorkspace = workspaceFilter === 'all' || (website.workspace || 'firma') === workspaceFilter;

      return matchesSearch && matchesExpiry && matchesCategory && matchesWorkspace;
    });
  }, [websites, searchTerm, expiryFilter, categoryFilter, workspaceFilter, rejectedSet]);

  const workspaceCounts = useMemo(() => {
    let firma = 0, side = 0;
    for (const w of websites) {
      if ((w.workspace || 'firma') === 'sideprojects') side++;
      else firma++;
    }
    return { firma, side };
  }, [websites]);

  // ===== Test połączenia WP (szkic → delete) =====
  const handleTestConnection = async (website: Website) => {
    setTestingIds(prev => new Set(prev).add(website.id));
    try {
      const res = await testWebsiteConnection(website);
      patchWebsiteLocal(website.id, {
        connection_status: res.ok ? 'ok' : 'error',
        connection_checked_at: new Date().toISOString(),
        connection_error: res.error || null,
      });
    } finally {
      setTestingIds(prev => {
        const next = new Set(prev);
        next.delete(website.id);
        return next;
      });
    }
  };

  // Cele bulku — przy włączonym skipConfirmed odpadają domeny już potwierdzone (OK)
  const bulkTargets = useMemo(
    () => skipConfirmed
      ? filteredWebsites.filter(w => w.connection_status !== 'ok')
      : filteredWebsites,
    [filteredWebsites, skipConfirmed],
  );

  const handleBulkTest = async () => {
    if (bulkTesting || bulkTargets.length === 0) return;
    const targets = bulkTargets;
    setBulkTesting(true);
    setBulkProgress({ done: 0, total: targets.length });
    setTestingIds(new Set(targets.map(w => w.id)));
    try {
      await testWebsitesBulk(targets, (done, total, current, result) => {
        setBulkProgress({ done, total });
        setTestingIds(prev => {
          const next = new Set(prev);
          next.delete(current.id);
          return next;
        });
        patchWebsiteLocal(current.id, {
          connection_status: result.ok ? 'ok' : 'error',
          connection_checked_at: new Date().toISOString(),
          connection_error: result.error || null,
        });
      });
    } finally {
      setBulkTesting(false);
      setBulkProgress(null);
      setTestingIds(new Set());
    }
  };

  const formatCheckedAt = (iso?: string | null) => {
    if (!iso) return '';
    const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (diffMin < 1) return 'teraz';
    if (diffMin < 60) return `${diffMin} min temu`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `${h} h temu`;
    return `${Math.floor(h / 24)} d temu`;
  };

  const togglePasswordVisibility = (websiteId: string) => {
    setVisiblePasswords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(websiteId)) {
        newSet.delete(websiteId);
      } else {
        newSet.add(websiteId);
      }
      return newSet;
    });
  };

  const toggleColumnVisibility = (column: keyof typeof columnVisibility) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Stary formularz zapisywał puste pola jako literał 'EMPTY' — bez tego
  // filtra badge pokazywał "Zostało Infinity dni".
  const hasRealDate = (expiryDate?: string | null) => {
    const s = (expiryDate || '').trim();
    return !!s && s.toUpperCase() !== 'EMPTY' && !Number.isNaN(new Date(s).getTime());
  };

  const getExpiryBadgeVariant = (expiryDate?: string) => {
    if (!expiryDate) return 'secondary';

    const days = getDaysUntilExpiry(expiryDate);
    if (days < 0) return 'destructive';
    if (days <= 7) return 'destructive';
    if (days <= 30) return 'outline';
    return 'default';
  };

  const getExpiryText = (expiryDate?: string) => {
    if (!expiryDate) return 'Brak daty';

    const days = getDaysUntilExpiry(expiryDate);
    if (days < 0) return `Wygasło ${Math.abs(days)} dni temu`;
    if (days === 0) return 'Wygasa dzisiaj';
    if (days === 1) return 'Wygasa jutro';
    return `Zostało ${days} dni`;
  };

  // Form handlers
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingWebsite) {
      // Ręczna zmiana daty odznacza źródło jako 'manual' — inaczej wiersz dalej
      // twierdziłby "auto · 2 h temu", choć data przyszła z klawiatury.
      const dateChanged = (formData.expiry_date || '') !== (editingWebsite.expiry_date || '');
      await updateWebsite(editingWebsite.id, dateChanged ? { ...formData, expiry_source: 'manual' } : formData);
    } else {
      await createWebsite(formData);
    }

    resetForm();
    setIsAddDialogOpen(false);
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const lines = bulkText.split('\n').filter(line => line.trim());
    const websitesToAdd: NewWebsite[] = [];

    for (const line of lines) {
      const parts = line.split(';').map(part => part.trim());
      if (parts.length >= 2) {
        websitesToAdd.push({
          name: parts[0] || 'EMPTY',
          url: parts[1] || 'EMPTY',
          login: parts[2] || 'EMPTY',
          password: parts[3] || 'EMPTY',
          api_key: parts[4] || 'EMPTY',
          category: parts[5] || 'EMPTY',
          expiry_date: parts[6] || 'EMPTY',
          provider: parts[7] || 'EMPTY',
        });
      }
    }

    if (websitesToAdd.length > 0) {
      await createWebsitesBulk(websitesToAdd);
      setBulkText('');
      setIsBulkAddDialogOpen(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      url: '',
      login: 'EMPTY',
      password: 'EMPTY',
      api_key: 'EMPTY',
      expiry_date: 'EMPTY',
      provider: 'EMPTY',
      category: 'EMPTY',
      workspace: 'firma'
    });
    setEditingWebsite(null);
    setShowPasswordInForm(false);
  };

  const startEdit = (website: Website) => {
    setEditingWebsite(website);
    setFormData({
      name: website.name,
      url: website.url,
      login: website.login || 'EMPTY',
      password: website.password || 'EMPTY',
      api_key: website.api_key || 'EMPTY',
      expiry_date: website.expiry_date || 'EMPTY',
      provider: website.provider || 'EMPTY',
      category: website.category || 'EMPTY',
      workspace: website.workspace || 'firma'
    });
    setShowPasswordInForm(false);
    setIsAddDialogOpen(true);
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Lista domen</CardTitle>
          <CardDescription>Ładowanie danych...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={`w-full max-w-full ${className}`}>
      <CardHeader className="pb-6">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Lista domen
            </CardTitle>
            <CardDescription>
              Zarządzaj swoimi domenami i ich konfiguracjami
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {/* Column visibility controls */}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Columns className="h-4 w-4 mr-2" />
                  Widoczność kolumn
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Widoczność kolumn</DialogTitle>
                  <DialogDescription>
                    Wybierz które kolumny mają być widoczne w tabeli
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3 py-4">
                  {Object.entries(columnVisibility).map(([column, isVisible]) => (
                    <div key={column} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={column}
                        checked={isVisible}
                        onChange={() => toggleColumnVisibility(column as keyof typeof columnVisibility)}
                        className="rounded border-gray-300"
                      />
                      <label htmlFor={column} className="text-sm font-medium">
                        {column === 'name' ? 'Nazwa' :
                         column === 'url' ? 'URL' :
                         column === 'login' ? 'Login' :
                         column === 'password' ? 'Hasło' :
                         column === 'api_key' ? 'API Key' :
                         column === 'provider' ? 'Provider' :
                         column === 'category' ? 'Kategoria' :
                         column === 'workspace' ? 'Workspace' :
                         column === 'connection' ? 'Status WP' :
                         column === 'expiry_date' ? 'Wygaśnięcie' :
                         column}
                      </label>
                    </div>
                  ))}
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={isBulkAddDialogOpen} onOpenChange={setIsBulkAddDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Dodaj bulk
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Dodaj domeny hurtowo</DialogTitle>
                  <DialogDescription>
                    Wklej dane domen w formacie: nazwa;url;login;hasło;api_key;kategoria;data_wygaśnięcia;provider
                    <br />
                    Każda domena w osobnej linii. Puste pola będą oznaczone jako "EMPTY"
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBulkSubmit}>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="przyklad.com;https://przyklad.com;admin;password123;api123;blog;2025-12-31;HomePL&#10;nazwa2.pl;https://nazwa2.pl;user;pass456;;sklep;2026-01-15;Hostinger&#10;domena3.pl;https://domena3.pl;;;EMPTY;firma;;EMPTY"
                      value={bulkText}
                      onChange={(e) => setBulkText(e.target.value)}
                      rows={8}
                    />
                  </div>
                  <DialogFooter className="mt-4">
                    <Button type="button" variant="outline" onClick={() => setIsBulkAddDialogOpen(false)}>
                      Anuluj
                    </Button>
                    <Button type="submit">Dodaj domeny</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
              setIsAddDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Dodaj domenę
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>
                    {editingWebsite ? 'Edytuj domenę' : 'Dodaj nową domenę'}
                  </DialogTitle>
                  <DialogDescription>
                    Wypełnij dane domeny i jej konfigurację
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleFormSubmit}>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nazwa domeny *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="url">URL *</Label>
                      <Input
                        id="url"
                        type="url"
                        value={formData.url}
                        onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login">Login</Label>
                      <Input
                        id="login"
                        value={formData.login}
                        onChange={(e) => setFormData(prev => ({ ...prev, login: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Hasło</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPasswordInForm ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPasswordInForm(!showPasswordInForm)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                        >
                          {showPasswordInForm ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="api_key">API Key</Label>
                      <Input
                        id="api_key"
                        value={formData.api_key}
                        onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expiry_date">Data wygaśnięcia</Label>
                      <Input
                        id="expiry_date"
                        type="date"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="provider">Provider</Label>
                      <Input
                        id="provider"
                        value={formData.provider}
                        onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value }))}
                        placeholder="np. HomePL, OVH, nazwa firmy"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Kategoria</Label>
                      <Input
                        id="category"
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        placeholder="np. blog, sklep, firma"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="workspace">Workspace</Label>
                      <Select
                        value={formData.workspace || 'firma'}
                        onValueChange={(v) => setFormData(prev => ({ ...prev, workspace: v as 'firma' | 'sideprojects' }))}
                      >
                        <SelectTrigger id="workspace">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="firma">🏢 Firma</SelectItem>
                          {canSideprojects && <SelectItem value="sideprojects">🎨 Side projects</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter className="mt-6">
                    <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Anuluj
                    </Button>
                    <Button type="submit">
                      {editingWebsite ? 'Zaktualizuj' : 'Dodaj domenę'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-6 mt-6 mb-8 p-6 bg-muted/20 rounded-lg border">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Szukaj domen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <Select value={expiryFilter} onValueChange={setExpiryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue>
                {expiryFilter === 'all' && 'Wszystkie domeny'}
                {expiryFilter === 'expiring-soon' && 'Wygasają wkrótce (≤30 dni)'}
                {expiryFilter === 'expired' && 'Wygasłe domeny'}
                {expiryFilter === 'rejected' && `Odrzucone (${rejectedInPanel.length})`}
                {expiryFilter === 'not-on-account' && 'Brak na koncie AfterMarket'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie domeny</SelectItem>
              <SelectItem value="expiring-soon">Wygasają wkrótce (≤30 dni)</SelectItem>
              <SelectItem value="expired">Wygasłe domeny</SelectItem>
              <SelectItem value="rejected">Odrzucone ({rejectedInPanel.length})</SelectItem>
              <SelectItem value="not-on-account">Brak na koncie AfterMarket</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-48">
              <SelectValue>
                {categoryFilter === 'all' && 'Wszystkie kategorie'}
                {categoryFilter !== 'all' && categoryFilter}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Wszystkie kategorie</SelectItem>
              {categories.map(category => (
                <SelectItem key={category} value={category}>
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={workspaceFilter} onValueChange={(v) => setWorkspaceFilter(v as 'all' | 'firma' | 'sideprojects')}>
            <SelectTrigger className="w-52">
              <SelectValue>
                {workspaceFilter === 'all' && `Wszystkie workspace (${workspaceCounts.firma + workspaceCounts.side})`}
                {workspaceFilter === 'firma' && `🏢 Firma (${workspaceCounts.firma})`}
                {workspaceFilter === 'sideprojects' && `🎨 Side projects (${workspaceCounts.side})`}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {/* "Wszystkie" tylko dla uprawnionych do Sponsoraków — zespołowi RLS
                  i tak zwraca samą firmę, więc UI nie sugeruje że istnieje coś więcej */}
              {canSideprojects && (
                <SelectItem value="all">Wszystkie workspace ({workspaceCounts.firma + workspaceCounts.side})</SelectItem>
              )}
              <SelectItem value="firma">🏢 Firma ({workspaceCounts.firma})</SelectItem>
              {canSideprojects && <SelectItem value="sideprojects">🎨 Side projects ({workspaceCounts.side})</SelectItem>}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-3">
            {/* Sync dat podąża za filtrem workspace: firma → konto firmowe,
                sideprojects → konto Sponsoraków, wszystkie → oba po kolei. */}
            <DomainExpirySync
              websites={websites}
              workspaces={
                workspaceFilter === 'all'
                  ? (canSideprojects ? ['firma', 'sideprojects'] : ['firma'])
                  : workspaceFilter === 'sideprojects' && !canSideprojects
                    ? []
                    : [workspaceFilter]
              }
              onSynced={refreshWebsites}
            />

            <RejectedDomainsDialog
              rejections={rejections}
              stillInPanel={rejectedInPanel.length}
              onSave={replaceList}
            />

            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkTest}
              disabled={bulkTesting || bulkTargets.length === 0}
              title="Tworzy szkic testowy na każdej domenie z listy i od razu go usuwa — sprawdza czy publikacja przejdzie"
            >
              {bulkTesting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testuję {bulkProgress?.done ?? 0}/{bulkProgress?.total ?? 0}…
                </>
              ) : (
                <>
                  <PlugZap className="h-4 w-4 mr-2" />
                  Sprawdź połączenia ({bulkTargets.length})
                </>
              )}
            </Button>
            <label
              className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap"
              title="Domeny ze statusem OK nie są testowane ponownie — bulk leci tylko po błędach i nietestowanych"
            >
              <input
                type="checkbox"
                checked={skipConfirmed}
                onChange={(e) => setSkipConfirmed(e.target.checked)}
                disabled={bulkTesting}
                className="rounded border-gray-300"
              />
              Pomiń potwierdzone (OK)
            </label>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-2">
        <div className="mx-2">
          <RejectedDomainsBanner
            matched={rejectedInPanel}
            onDeleteWebsite={deleteWebsite}
            onUnreject={removeRejection}
            domainKey={websiteDomainKey}
          />
        </div>

        <div className="border rounded-lg overflow-hidden shadow-lg mx-2">
          <div className="overflow-x-auto">
            <Table className="min-w-full table-auto w-full">
            <TableHeader>
              <TableRow className="bg-muted/50">
                {columnVisibility.name && <TableHead className="min-w-[180px] py-3 px-4">Nazwa</TableHead>}
                {columnVisibility.url && <TableHead className="min-w-[200px] py-3 px-4">URL</TableHead>}
                {columnVisibility.login && <TableHead className="min-w-[120px] py-3 px-4">Login</TableHead>}
                {columnVisibility.password && <TableHead className="min-w-[140px] py-3 px-4">Hasło</TableHead>}
                {columnVisibility.api_key && <TableHead className="min-w-[180px] py-3 px-4">API Key</TableHead>}
                {columnVisibility.provider && <TableHead className="min-w-[120px] py-3 px-4">Provider</TableHead>}
                {columnVisibility.category && <TableHead className="min-w-[120px] py-3 px-4">Kategoria</TableHead>}
                {columnVisibility.workspace && <TableHead className="min-w-[110px] py-3 px-4">Workspace</TableHead>}
                {columnVisibility.connection && <TableHead className="min-w-[150px] py-3 px-4">Status WP</TableHead>}
                {columnVisibility.expiry_date && <TableHead className="min-w-[150px] py-3 px-4">Wygaśnięcie</TableHead>}
                <TableHead className="min-w-[120px] py-3 px-4">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWebsites.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={Object.values(columnVisibility).filter(Boolean).length + 1}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {websites.length === 0 ? 'Brak domen. Dodaj pierwszą domenę.' : 'Brak domen spełniających kryteria filtrowania.'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredWebsites.map((website) => {
                  const safeUrl = getSafeUrl(website.url);
                  return (
                  <TableRow key={website.id} className="hover:bg-muted/30 transition-colors">
                    {columnVisibility.name && (
                      <TableCell className="font-medium py-3 px-4">{website.name}</TableCell>
                    )}
                    {columnVisibility.url && (
                      <TableCell className="py-3 px-4">
                        {safeUrl ? (
                          <a
                            href={safeUrl || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 underline break-all"
                          >
                            {website.url}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">{website.url}</span>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.login && (
                      <TableCell className="py-3 px-4">
                        {website.login ? (
                          <span className="flex items-center gap-2">
                            <Key className="h-4 w-4" />
                            {website.login}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.password && (
                      <TableCell className="py-3 px-4">
                        {website.password ? (
                          <div className="flex items-center gap-2">
                            {visiblePasswords.has(website.id) ? (
                              <>
                                <span className="text-sm font-mono">{website.password}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => togglePasswordVisibility(website.id)}
                                  className="h-6 w-6 p-0 hover:bg-transparent"
                                >
                                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <span className="text-muted-foreground">••••••••</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => togglePasswordVisibility(website.id)}
                                  className="h-6 w-6 p-0 hover:bg-transparent"
                                >
                                  <Eye className="h-4 w-4 text-muted-foreground" />
                                </Button>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.api_key && (
                      <TableCell className="py-3 px-4">
                        {website.api_key ? (
                          <span className="text-sm font-mono break-all">
                            {website.api_key.length > 25 ? `${website.api_key.substring(0, 25)}...` : website.api_key}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.provider && (
                      <TableCell className="py-3 px-4">
                        {website.provider ? (
                          <Badge variant="outline" className="flex items-center gap-2 px-3 py-1">
                            <Server className="h-4 w-4" />
                            {website.provider}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.category && (
                      <TableCell className="py-3 px-4">
                        {website.category ? (
                          <Badge variant="secondary" className="px-3 py-1">{website.category}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.workspace && (
                      <TableCell className="py-3 px-4">
                        {(website.workspace || 'firma') === 'sideprojects' ? (
                          <Badge variant="outline" className="px-2.5 py-1 border-fuchsia-500/40 text-fuchsia-400 bg-fuchsia-500/10 flex items-center gap-1.5 w-fit">
                            <Palette className="h-3 w-3" />
                            Side
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="px-2.5 py-1 border-blue-500/40 text-blue-400 bg-blue-500/10 flex items-center gap-1.5 w-fit">
                            <Briefcase className="h-3 w-3" />
                            Firma
                          </Badge>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.connection && (
                      <TableCell className="py-3 px-4">
                        {testingIds.has(website.id) ? (
                          <Badge variant="outline" className="px-2.5 py-1 flex items-center gap-1.5 w-fit text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            testuję…
                          </Badge>
                        ) : website.connection_status === 'ok' ? (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="px-2.5 py-1 border-emerald-500/40 text-emerald-400 bg-emerald-500/10 flex items-center gap-1.5 w-fit cursor-default">
                                  <CheckCircle2 className="h-3 w-3" />
                                  OK · {formatCheckedAt(website.connection_checked_at)}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[280px]">
                                <p className="text-xs">
                                  Szkic testowy utworzony i usunięty — publikacja przejdzie.
                                  {website.connection_error ? ` Uwaga: ${website.connection_error}` : ''}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : website.connection_status === 'error' ? (
                          <TooltipProvider delayDuration={200}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="px-2.5 py-1 border-red-500/40 text-red-400 bg-red-500/10 flex items-center gap-1.5 w-fit cursor-default">
                                  <XCircle className="h-3 w-3" />
                                  Błąd · {formatCheckedAt(website.connection_checked_at)}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[320px]">
                                <p className="text-xs break-words">{website.connection_error || 'Nieznany błąd'}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-muted-foreground text-xs">nie testowano</span>
                        )}
                      </TableCell>
                    )}
                    {columnVisibility.expiry_date && (
                      <TableCell className="py-3 px-4">
                        <div className="flex flex-col items-start gap-1.5">
                          {hasRealDate(website.expiry_date) ? (
                            <Badge variant={getExpiryBadgeVariant(website.expiry_date)} className="flex items-center gap-2 px-3 py-1">
                              <Calendar className="h-4 w-4" />
                              {getExpiryText(website.expiry_date)}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">brak daty</span>
                          )}

                          {/* Skąd wzięła się data — po sync widać, czy jest świeża czy klepana ręcznie */}
                          {website.expiry_source === 'not_on_account' ? (
                            <span
                              className="text-[11px] text-amber-400 flex items-center gap-1"
                              title="Domeny nie ma na koncie AfterMarket — przepadła albo jest u innego rejestratora. Data poniżej to ostatnia znana."
                            >
                              <AlertTriangle className="h-3 w-3" />
                              brak na koncie
                            </span>
                          ) : website.expiry_source === 'aftermarket' ? (
                            <span
                              className="text-[11px] text-muted-foreground"
                              title={`Pobrane z AfterMarket${website.am_autorenew === false ? ' · autorenew WYŁĄCZONY' : website.am_autorenew ? ' · autorenew włączony' : ''}`}
                            >
                              auto · {formatCheckedAt(website.expiry_synced_at)}
                              {website.am_autorenew === false && ' · bez autorenew'}
                            </span>
                          ) : (website.workspace || 'firma') === 'sideprojects' ? (
                            <span className="text-[11px] text-muted-foreground" title="Jeszcze nie sprawdzona w AfterMarket (konto Sponsoraków) — przefiltruj na Side projects i kliknij „Sprawdź daty w AfterMarket”">
                              ręcznie
                            </span>
                          ) : null}

                          {isRejected(website) && (
                            <Badge
                              variant="outline"
                              className="px-2 py-0.5 text-[11px] border-destructive/50 text-destructive bg-destructive/10 flex items-center gap-1"
                              title="Domena jest na liście odrzuconych — nie będzie przedłużona. Usuń ją z panelu, gdy potwierdzisz, że może zniknąć."
                            >
                              <Ban className="h-3 w-3" />
                              ODRZUCONA — nie przedłużamy
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(website)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSchemaDialogWebsite(website)}
                          className={`h-8 w-8 p-0 ${website.schema_enabled ? "text-indigo-500 hover:text-indigo-600" : "text-muted-foreground"}`}
                          title={website.schema_enabled ? "Schema.org: włączona" : "Schema.org: konfiguruj"}
                        >
                          <FileCode2 className="h-3 w-3" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTestConnection(website)}
                          disabled={testingIds.has(website.id) || bulkTesting}
                          className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-600"
                          title="Test połączenia WP — tworzy szkic i od razu go usuwa"
                        >
                          {testingIds.has(website.id) ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <PlugZap className="h-3 w-3" />
                          )}
                        </Button>

                        {website.expiry_date && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => renewWebsiteExpiry(website.id)}
                            className="h-8 w-8 p-0"
                            title="Domena odnowiona"
                          >
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        )}

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Usuń domenę</AlertDialogTitle>
                              <AlertDialogDescription>
                                Czy na pewno chcesz usunąć domenę <strong>{website.name}</strong>?
                                <br />
                                Tej operacji nie można cofnąć.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Anuluj</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteWebsite(website.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Usuń
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                )})
              )}
            </TableBody>
          </Table>
          </div>
        </div>
      </CardContent>

      <WebsiteSchemaDialog
        website={schemaDialogWebsite}
        open={!!schemaDialogWebsite}
        onOpenChange={(o) => { if (!o) setSchemaDialogWebsite(null); }}
      />
    </Card>
  );
};

export default WebsitesTable;
